import type { AppThunk } from "~/app/store";
import type { AddToCartSubmitData } from "~/features/addToCart/hooks/useAddToCartForm";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { isPackageRestrictionEnabled } from "~/features/PackageRestriction/utils/isPackageRestrictionEnabled";
import { calculateSalesCampaign4PDP } from "~/features/salesCampaign/utils/salesCampaignUtils";
import type { PurchasePlace, PurchaseType } from "~/app/analytics/ga/recommendations/types";
import type { AddToCartItem, AddToCartPartItem } from "~/features/addToCart/redux/addToCart/productAddFlow";

type ProductDescriptorForAddToCart = AddToCartSubmitData["derived"]["descriptor"];

export interface BasicAddToCartItem {
  productCode: string;
  packCode: string;
  quantity: number;
  priceReference: string;
  rdd?: Date | null;
  isSale: boolean;
  comment?: string;
}

export function addToCartItemFromSubmitData({
  submitData,
  productCode,
  restrictionQuery,
  context,
  position,
}: {
  submitData: AddToCartSubmitData & { rdd?: Date };
  productCode: string;
  restrictionQuery: string;
  context?: {
    page: PurchasePlace;
    recommendationType?: PurchaseType;
    recommendationPlace?: PurchasePlace;
  };
  position?: number;
}): AddToCartItem {
  const descriptor = submitData.derived.descriptor;
  const product = descriptor.base;
  const isRestricted = !!(
    product.sapMaterialNumber &&
    restrictionQuery?.includes(product.sapMaterialNumber) &&
    isPackageRestrictionEnabled()
  );

  return addToCartItemFromSubmitDataWithDescriptor({
    submitData,
    descriptor,
    isRestricted,
    context,
    position,
  });
}

export function addToCartItemsFromSubmitData({
  items,
  restrictionQuery,
  context,
}: {
  items: (AddToCartSubmitData & { productCode: string; rdd?: Date; position?: number })[];
  restrictionQuery: string;
  context?: {
    page: PurchasePlace;
    recommendationType?: PurchaseType;
    recommendationPlace?: PurchasePlace;
  };
}): AddToCartItem[] {
  return items.map((item) =>
    addToCartItemFromSubmitData({
      submitData: item,
      productCode: item.productCode,
      restrictionQuery,
      context,
      position: item.position,
    }),
  );
}

export function addToCartItemsFromBasicItems(
  items: BasicAddToCartItem[],
  options: {
    restrictionQuery?: string;
    context?: {
      page: PurchasePlace;
      recommendationType?: PurchaseType;
      recommendationPlace?: PurchasePlace;
    };
  } = {},
): AppThunk<Promise<AddToCartItem[]>> {
  return async (dispatch) => {
    // Load each item's descriptor through the canonical PnA queries — no legacy Redux PnA
    // reread; the thunk stages missing basic products and returns the loaded descriptor.
    const descriptors = await Promise.all(
      items.map(
        (item) =>
          dispatch(ProductService.getProductDescriptor(item.productCode, item.priceReference, { fetch: true }))
            .promise!,
      ),
    );

    return items.map((item, index) => addToCartItemFromBasicItem(item, descriptors[index], options));
  };
}

function addToCartItemFromSubmitDataWithDescriptor({
  submitData,
  descriptor,
  isRestricted,
  context,
  position,
}: {
  submitData: AddToCartSubmitData & { rdd?: Date };
  descriptor: ProductDescriptorForAddToCart;
  isRestricted: boolean;
  context?: {
    page: PurchasePlace;
    recommendationType?: PurchaseType;
    recommendationPlace?: PurchasePlace;
  };
  position?: number;
}): AddToCartItem {
  const { formValues, derived } = submitData;
  const priceReference = formValues.priceReference;
  const baseInfo = descriptor.base;
  const packageCode = formValues.selectedPackCode;
  const selectedPackage = derived.selectedPack;
  const packageSize = derived.selectedPackaging?.packSize ?? selectedPackage?.packSize ?? 1;
  const fifteenDigit = baseInfo.fifteenDigit[packageCode];

  return {
    productCode: baseInfo.hybrisCode,
    packageSize,
    packageCode,
    quantity: formValues.quantity,
    comment: formValues.comment ?? "",
    selectedDeliveryDate: submitData.rdd ?? null,
    selectedDeliveryDateIsManual: !!submitData.rdd,
    primaryWarehouseCode: selectedPackage?.primaryWarehouseCode ?? "",
    priceReference,
    restrictedPackage: isRestricted,
    optionalParts: prepareOptionalParts(submitData, descriptor),
    position,
    originCountry: fifteenDigit?.countryOfOrigin ?? "",
    salesCampaignCode: resolveSalesCampaignCode({
      salePriceEnabled: formValues.salePriceEnabled,
      packageCode,
      descriptor,
    }),
    recommendationPlace: context?.recommendationPlace,
    recommendationType: context?.recommendationType,
  };
}

function addToCartItemFromBasicItem(
  item: BasicAddToCartItem,
  descriptor: ProductDescriptorForAddToCart,
  {
    restrictionQuery = "",
    context,
  }: {
    restrictionQuery?: string;
    context?: {
      page: PurchasePlace;
      recommendationType?: PurchaseType;
      recommendationPlace?: PurchasePlace;
    };
  },
): AddToCartItem {
  const packaging = ProductService.findPackagingByCode(descriptor, item.packCode);
  const fifteenDigit = descriptor.base.fifteenDigit[item.packCode];
  const sapNo = descriptor.base.sapMaterialNumber;

  return {
    productCode: descriptor.base.hybrisCode,
    packageSize: packaging.packSize,
    packageCode: item.packCode,
    quantity: item.quantity,
    comment: item.comment,
    selectedDeliveryDate: item.rdd ?? null,
    selectedDeliveryDateIsManual: !!item.rdd,
    primaryWarehouseCode: packaging.primaryWarehouseCode,
    priceReference: item.priceReference,
    restrictedPackage: !!(sapNo && restrictionQuery.includes(sapNo) && isPackageRestrictionEnabled()),
    originCountry: fifteenDigit?.countryOfOrigin ?? "",
    salesCampaignCode: resolveSalesCampaignCode({
      salePriceEnabled: item.isSale,
      packageCode: item.packCode,
      descriptor,
    }),
    recommendationPlace: context?.recommendationPlace,
    recommendationType: context?.recommendationType,
  };
}

function prepareOptionalParts(
  submitData: AddToCartSubmitData,
  descriptor: ProductDescriptorForAddToCart,
): AddToCartPartItem[] {
  return Object.entries(submitData.optionalParts)
    .filter(([, part]) => part.formValues.selected)
    .map(([, part]) => {
      const partProductDescriptor = part.derived.descriptor;
      const packaging = ProductService.findPackagingByCode(partProductDescriptor, part.formValues.selectedPackCode);
      const fifteenDigit = partProductDescriptor.base.fifteenDigit[part.formValues.selectedPackCode];

      return {
        productCode: part.formValues.productId,
        packageCode: part.formValues.selectedPackCode,
        packageSize: packaging.packSize,
        quantity: part.formValues.quantity,
        manualUpdated: part.meta.isQtyUserModified,
        primaryWarehouseCode: packaging.primaryWarehouseCode,
        partTypeCode: ProductService.findOptionalPartById(descriptor, part.formValues.productId)!.type,
        originCountry: fifteenDigit?.countryOfOrigin ?? "",
      };
    });
}

function resolveSalesCampaignCode({
  salePriceEnabled,
  packageCode,
  descriptor,
}: {
  salePriceEnabled: boolean;
  packageCode: string;
  descriptor: ProductDescriptorForAddToCart;
}): string | undefined {
  if (!salePriceEnabled) {
    return undefined;
  }

  const saleData = calculateSalesCampaign4PDP({ productDescriptor: descriptor, includeSoldOut: true });
  const saleForPack = saleData.data?.salePerPack[packageCode];
  return saleForPack?.salesStock ? saleForPack.campaignCode : undefined;
}
